/**
 * Utility functions for page config related functionality in search
 */
import SearchExperiments from 'bundles/epic/clients/search';

/**
 * List of queries that should use the page config system for free content experience
 */
export const FREE_QUERIES = [
  'free',
  'free courses with certificate',
  'free courses',
  'free certificate courses',
  'free course with certificate',
  'free  courses with certificate',
  'free course',
  'free/',
  'free certificate',
  'free guided projects',
  'free certificate course',
  'free degree',
  'free online courses',
  'free certification',
  'free certificat courses',
  'free cources',
  'free courses with free certificate',
  'free courses with certificates',
  'free  courses',
  'free certification courses',
  'free certifications',
  '1 hour free',
  'free course less than 2 hours',
  'free projects',
  '/free',
  'free with certificate',
  'free online course',
  'free certification course',
  'free corse',
  'free online courses with certificate',
];

/**
 * Determines if a search query should use the page config system for free content
 */
export const isFreeQuery = (query?: string | string[]): boolean => {
  if (!query) return false;
  let normalizedQuery = query;
  if (Array.isArray(normalizedQuery)) {
    normalizedQuery = normalizedQuery.filter(Boolean)[0];
  }
  if (!normalizedQuery) return false;
  normalizedQuery = normalizedQuery.trim().toLowerCase();
  return FREE_QUERIES.includes(normalizedQuery);
};

export const isInFreeQueryExperimentImpression = (): boolean => {
  return SearchExperiments.get('freeQuery') === true;
};

export const isInFreeQueryExperiment = (): boolean => {
  return SearchExperiments.preview('freeQuery') === true;
};

/**
 * Determines if a search should use the page config system
 * based on query content and experiment status, impress only if they are part of FREE_QUERIES
 */
export const shouldShowFreeQueryPageConfig = (query?: string): boolean => {
  return isFreeQuery(query) && isInFreeQueryExperimentImpression();
};

export const shouldShowFreeQueryPageConfigPreview = (query?: string): boolean => {
  return isFreeQuery(query) && isInFreeQueryExperiment();
};

type FreeQueryCounts = {
  freeCount: number;
  freeTrialCount: number;
};

/**
 * To update these hardcoded values with real search counts:
 * 1. Copy the FREE_QUERIES array above to the script at: static/bundles/search-common/scripts/fetch-free-query-counts.js
 * 2. Run the script: node static/bundles/search-common/scripts/fetch-free-query-counts.js
 * 3. Copy the output from the script and replace the freeQueryCounts object below
 */
const freeQueryCounts: Record<string, FreeQueryCounts> = {
  free: { freeCount: 58, freeTrialCount: 434 },
  'free courses with certificate': { freeCount: 239, freeTrialCount: 5539 },
  'free courses': { freeCount: 66, freeTrialCount: 1552 },
  'free certificate courses': { freeCount: 68, freeTrialCount: 1825 },
  'free course with certificate': { freeCount: 298, freeTrialCount: 6645 },
  'free  courses with certificate': { freeCount: 239, freeTrialCount: 5539 },
  'free course': { freeCount: 290, freeTrialCount: 5742 },
  'free/': { freeCount: 58, freeTrialCount: 432 },
  'free certificate': { freeCount: 63, freeTrialCount: 872 },
  'free guided projects': { freeCount: 112, freeTrialCount: 1217 },
  'free certificate course': { freeCount: 291, freeTrialCount: 5881 },
  'free degree': { freeCount: 60, freeTrialCount: 726 },
  'free online courses': { freeCount: 107, freeTrialCount: 2045 },
  'free certification': { freeCount: 64, freeTrialCount: 846 },
  'free certificat courses': { freeCount: 67, freeTrialCount: 1576 },
  'free cources': { freeCount: 68, freeTrialCount: 424 },
  'free courses with free certificate': { freeCount: 239, freeTrialCount: 5540 },
  'free courses with certificates': { freeCount: 239, freeTrialCount: 5534 },
  'free  courses': { freeCount: 66, freeTrialCount: 1552 },
  'free certification courses': { freeCount: 69, freeTrialCount: 1835 },
  'free certifications': { freeCount: 58, freeTrialCount: 553 },
  '1 hour free': { freeCount: 129, freeTrialCount: 1258 },
  'free course less than 2 hours': { freeCount: 312, freeTrialCount: 5959 },
  'free projects': { freeCount: 109, freeTrialCount: 1141 },
  '/free': { freeCount: 58, freeTrialCount: 439 },
  'free with certificate': { freeCount: 237, freeTrialCount: 5257 },
  'free online course': { freeCount: 314, freeTrialCount: 5904 },
  'free certification course': { freeCount: 290, freeTrialCount: 5825 },
  'free corse': { freeCount: 69, freeTrialCount: 442 },
  'free online courses with certificate': { freeCount: 270, freeTrialCount: 5717 },
};

export const freeQueriesValuePropCounts = (query: string): FreeQueryCounts => {
  return freeQueryCounts[query] || { freeCount: 0, freeTrialCount: 0 };
};
